import AxonIcon from "components/common/icons/PartnerIcons/AxonIcon";
import DedroneIcon from "components/common/icons/PartnerIcons/DedroneIcon";
import ShowDroneOnMapButton from "components/main/teleop/common/ShowDroneOnMapButton";

import type { JSX } from "react";
import type { AlertInstance } from "./slice";

export type AlertLevel = "warning" | "caution" | "advisory";
type AudioConfig = { path: string; repeat?: boolean };
export type AlertConfig = {
  title: string;
  description?: React.ReactNode;
  level: AlertLevel;
  audio?: AudioConfig;
  priority: number;
  /**
   * If provided, will be passed into the AlertIcon component as a child
   */
  customIcon?: JSX.Element;
  /**
   * This is only for AXON CEO Summit Demo - will restyle the Alert.
   */
  _isDedroneDemoAlert?: boolean;
};
export type ActiveAlertDetails = AlertInstance & AlertConfig;

/**
 * Defines the audio paths for all alerts. We define them in this array to ensure we cache all of
 * them when the alert manager is initialized
 */
const TRAFFIC_AUDIO_PATH = "/audio/traffic.mp3";
const ELAND_AUDIO_PATH = "/audio/e-land.mp3";
const LINK_LOSS_AUDIO_PATH = "/audio/link-loss.mp3";
const CAUTION_BEEP_AUDIO_PATH = "/audio/caution-beep.mp3";
const WARNING_BEEP_AUDIO_PATH = "/audio/warning-beep.mp3";
const PARACHUTE_DEPLOYED_AUDIO_PATH = "/audio/parachute-deployed.mp3";
const AIRCRAFT_INCOMING_LANDING_AUDIO_PATH = "/audio/aircraft-incoming-landing-now.mp3";
export const ALERT_AUDIO_PATHS = [
  TRAFFIC_AUDIO_PATH,
  ELAND_AUDIO_PATH,
  LINK_LOSS_AUDIO_PATH,
  CAUTION_BEEP_AUDIO_PATH,
  WARNING_BEEP_AUDIO_PATH,
  PARACHUTE_DEPLOYED_AUDIO_PATH,
  AIRCRAFT_INCOMING_LANDING_AUDIO_PATH,
];

/**
 * Defines all the alerts that are managed by the alert manager
 * We define them here to ensure there is not overlapping of priority
 * number between alerts
 */
export const ALERT_CONFIGS = {
  ADSB_TRAFFIC_MUTED: (variant?: string): AlertConfig => ({
    level: "caution",
    priority: 0,
    title: "Air Traffic Alert",
    description: variant ? variant : "Execute safe state maneuver and monitor the map",
  }),
  ADSB_TRAFFIC: (variant?: string): AlertConfig => ({
    level: "warning",
    priority: 0,
    title: "Air Traffic Alert",
    description: variant ? variant : "Execute safe state maneuver and monitor the map",
    audio: { path: TRAFFIC_AUDIO_PATH, repeat: true },
  }),
  ADSB_AUTO_RESPONSE: (variant?: string): AlertConfig => ({
    level: variant === "land" ? "warning" : "caution",
    priority: -1,
    title: "Avoiding Air Traffic",
    description:
      variant === "descend"
        ? "Descending to 100ft AGL"
        : variant === "land"
          ? "Landing"
          : "Ceiling set to 100ft AGL",
    audio: variant === "land" ? { path: AIRCRAFT_INCOMING_LANDING_AUDIO_PATH } : undefined,
  }),
  DEDRONE_ALERT: (variant?: string): AlertConfig => ({
    level: "warning",
    priority: 0,
    title: "Dedrone Alert - Incoming Aircraft",
    description: variant ? variant : "Check the map view for more information.",
    audio: { path: TRAFFIC_AUDIO_PATH, repeat: true },
    customIcon: <DedroneIcon />,
    _isDedroneDemoAlert: true,
  }),
  EMERGENCY_LANDING: (variant?: string): AlertConfig => ({
    level: "warning",
    priority: 1,
    title: "Emergency Landing",
    description: variant ? variant : "Control aircraft to safe landing",
    audio: { path: ELAND_AUDIO_PATH, repeat: true },
  }),
  LOST_LINK_CAUTION: (variant?: string): AlertConfig => ({
    level: "caution",
    priority: 2,
    title: "Connection to Aircraft Lost",
    description: variant
      ? variant
      : "Vehicle will execute planned return after the connection timeout",
    audio: { path: CAUTION_BEEP_AUDIO_PATH },
  }),
  REMOTE_ID_FAULT: (variant?: string): AlertConfig => ({
    level: "caution",
    priority: 3,
    title: "Remote ID Inoperative",
    description: variant
      ? variant
      : "Check GPS function and connection of operator location source",
    audio: { path: CAUTION_BEEP_AUDIO_PATH },
  }),
  DEGRADED_LINK: (variant?: string): AlertConfig => ({
    level: "caution",
    priority: 4,
    title: "Weak Connection",
    description: variant ? variant : "",
    audio: { path: CAUTION_BEEP_AUDIO_PATH },
  }),
  SAFE_LANDING: (variant?: string): AlertConfig => ({
    level: "caution",
    priority: 5,
    title: "Safe Landing",
    description: variant ? variant : "Vehicle is returning to safe landing point",
    audio: { path: CAUTION_BEEP_AUDIO_PATH },
  }),
  GIMBAL_FLASHLIGHT_TOO_HOT: (variant?: string): AlertConfig => ({
    level: "advisory",
    priority: 6,
    title: "Flashlight Too Hot",
    description: variant ? variant : "The flashlight is being turned off due to overheating",
  }),
  ICING_RISK_DETECTED: (variant?: string): AlertConfig => ({
    level: "caution",
    priority: 6,
    title: "Icing Risk Detected",
    description: variant
      ? variant
      : "Weather conditions favorable for icing. Consider landing or returning to dock.",
  }),
  WEATHER_MARGINAL: (variant?: string): AlertConfig => ({
    level: "caution",
    priority: 6,
    title: "Marginal Weather Conditions",
    description: variant
      ? variant
      : "Reduced visibility or low clouds detected. Exercise caution and monitor conditions.",
    audio: { path: CAUTION_BEEP_AUDIO_PATH },
  }),
  WEATHER_BELOW_MINIMUM: (variant?: string): AlertConfig => ({
    level: "warning",
    priority: 1,
    title: "Weather Below Minimums",
    description: variant
      ? variant
      : "Visibility or cloud conditions are below safe flight minimums. Land immediately.",
    audio: { path: WARNING_BEEP_AUDIO_PATH, repeat: true },
  }),
  // This alert is not critical to flight safety. It is used to indicate to the pilot that an
  // officer is requesting drone support at their location.
  AXON_WATCH_ME: (variant?: string): AlertConfig => ({
    level: "caution",
    priority: 7,
    title: variant ? "Drone Requested : " + variant : "Drone Requested",
    customIcon: <AxonIcon />,
  }),
  PARACHUTE_DEPLOYED: (variant?: string): AlertConfig => ({
    level: "warning",
    priority: 8,
    title: "Parachute Deployed",
    description: variant ? (
      variant
    ) : (
      <div className="space-y-2">
        <div>All flight functionality of the drone has been terminated</div>
        <ShowDroneOnMapButton />
      </div>
    ),
    audio: { path: PARACHUTE_DEPLOYED_AUDIO_PATH, repeat: true },
  }),
  SUBJECT_DETECTED: (variant?: string): AlertConfig => ({
    level: "advisory",
    priority: 9,
    title: "Subject Detected",
    description: variant ?? "A subject has been detected and is being tracked",
    audio: { path: CAUTION_BEEP_AUDIO_PATH },
  }),
} satisfies { [key: string]: (variant?: string) => AlertConfig };

export type AlertType = keyof typeof ALERT_CONFIGS;
